import {
  runEditWorkspaceWorkflow,
  runMoveWorkspaceToTrashWorkflow,
  runShareWorkspaceWorkflow,
} from "@/action-dialog";
import type { ObjectActionHandlers } from "@/object-actions/types";
import type { Workspace } from "@cohesive/api-client";
import { useMemo } from "react";

export interface WorkspaceActionHandlerDeps {
  editWorkspace?: (workspace: Workspace) => void;
  /** Opens the members/share dialog (wired to the `add-members` action). */
  shareWorkspace?: (workspace: Workspace) => void;
  deleteWorkspace?: (workspace: Workspace) => void;
  /** Create a canvas at the workspace's top level. */
  newCanvas?: (workspace: Workspace) => void;
  /** Create a workspace chat at the workspace's top level. */
  newChat?: (workspace: Workspace) => void;
  /** Navigate to the workspace detail page (wired to the `open` action). */
  openWorkspace?: (workspace: Workspace) => void;
  /** Create a blank document at the workspace root. */
  newDocument?: (workspace: Workspace) => void;
  /** Create a folder at the workspace root. */
  newFolder?: (workspace: Workspace) => void;
  /** Open the file picker targeting the workspace root. */
  uploadFiles?: (workspace: Workspace) => void;
  /** Open the OS folder picker targeting the workspace root. */
  uploadFolder?: (workspace: Workspace) => void;
}

export function createWorkspaceActionHandlers(
  workspace: Workspace,
  deps: WorkspaceActionHandlerDeps,
): ObjectActionHandlers {
  return {
    "new-canvas": deps.newCanvas ? () => deps.newCanvas!(workspace) : undefined,
    "new-chat": deps.newChat ? () => deps.newChat!(workspace) : undefined,
    "new-document": deps.newDocument
      ? () => deps.newDocument!(workspace)
      : undefined,
    "new-folder": deps.newFolder ? () => deps.newFolder!(workspace) : undefined,
    "upload-files": deps.uploadFiles
      ? () => deps.uploadFiles!(workspace)
      : undefined,
    "upload-folder": deps.uploadFolder
      ? () => deps.uploadFolder!(workspace)
      : undefined,
    open: deps.openWorkspace ? () => deps.openWorkspace!(workspace) : undefined,
    "edit-workspace": deps.editWorkspace
      ? () => deps.editWorkspace!(workspace)
      : undefined,
    "add-members": deps.shareWorkspace
      ? () => deps.shareWorkspace!(workspace)
      : undefined,
    delete: deps.deleteWorkspace
      ? () => deps.deleteWorkspace!(workspace)
      : undefined,
  };
}

export function useWorkspaceActionHandlerDeps({
  updateWorkspace,
  deleteWorkspace,
  onDeleted,
}: {
  updateWorkspace: Parameters<
    typeof runEditWorkspaceWorkflow
  >[0]["updateWorkspace"];
  deleteWorkspace: Parameters<
    typeof runMoveWorkspaceToTrashWorkflow
  >[0]["deleteWorkspace"];
  onDeleted?: (workspace: Workspace) => void;
}) {
  return useMemo(
    (): WorkspaceActionHandlerDeps => ({
      editWorkspace: (workspace) =>
        void runEditWorkspaceWorkflow({ workspace, updateWorkspace }),
      shareWorkspace: (workspace) =>
        void runShareWorkspaceWorkflow({ workspace }),
      deleteWorkspace: (workspace) =>
        void runMoveWorkspaceToTrashWorkflow({
          workspace,
          deleteWorkspace,
          onSuccess: onDeleted,
        }),
    }),
    [deleteWorkspace, onDeleted, updateWorkspace],
  );
}
